import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TbHeadphones } from 'react-icons/tb';
import CenterModalWindow from '../CenterModalWindow/CenterModalWindow';
import ConfirmModal from '../ConfirmModal';
import AudioPlayerHeadWindowActions from './AudioPlayerHeadWindowActions';
import AudioPlayerMiniDock from './AudioPlayerMiniDock';
import AudioPlayerPlaylistPanel from './AudioPlayerPlaylistPanel';
import StorageBoxAudioBrowser from './StorageBoxAudioBrowser';
import StorageBoxAudioStudioLayout, { useCoarseMobileLayout } from './StorageBoxAudioStudioLayout';
import { StorageBoxFileAudioPlayer } from './StorageBoxAudioPlayer';
import { useAppShellHeaderOffset } from './useAppShellHeaderOffset';
import { useAudioPlaylist } from './useAudioPlaylist';

export default function StorageBoxFileAudioSession({
  fileItem,
  theme = 'auto',
  initialPlaylist = null,
  publicToken = null,
  presentation = 'inline',
  minimized: minimizedProp,
  onMinimizedChange = null,
  sessionApiRef = null,
  onFileSelected,
  onSessionClose = null,
}) {
  const mobileLayout = useCoarseMobileLayout();
  const [mobilePane, setMobilePane] = useState('player');
  const isControlledMinimized = typeof minimizedProp === 'boolean';
  const [minimizedLocal, setMinimizedLocal] = useState(false);
  const minimized = isControlledMinimized ? minimizedProp : minimizedLocal;
  const setMinimized = useCallback(
    (next) => {
      if (isControlledMinimized) onMinimizedChange?.(next);
      else setMinimizedLocal(next);
    },
    [isControlledMinimized, onMinimizedChange]
  );

  const playlist = useAudioPlaylist(fileItem, {
    enabled: Boolean(fileItem?.itemId),
    initialTracks: initialPlaylist?.tracks,
    initialMeta: initialPlaylist?.meta,
  });
  const { playItemNow, queueTrackPlayNext } = playlist;
  const [dockPlaying, setDockPlaying] = useState(false);
  const [sleepDockLine, setSleepDockLine] = useState('');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const playerApiRef = useRef(null);
  const seededFileItemIdRef = useRef('');

  const isModal = presentation === 'modal';
  useAppShellHeaderOffset(isModal && mobileLayout && !minimized);

  useEffect(() => {
    if (!sessionApiRef) return undefined;
    sessionApiRef.current = {
      playItemNow,
      queuePlayNext: queueTrackPlayNext,
    };
    return () => {
      sessionApiRef.current = null;
    };
  }, [sessionApiRef, playItemNow, queueTrackPlayNext]);

  useEffect(() => {
    const itemId = fileItem?.itemId;
    if (!itemId) {
      seededFileItemIdRef.current = '';
      return;
    }
    if (seededFileItemIdRef.current === itemId) return;
    seededFileItemIdRef.current = itemId;
    playItemNow(fileItem);
  }, [fileItem, playItemNow]);

  useEffect(() => {
    if (!isModal || !minimized) {
      document.body.classList.remove('sb-audio-player-dock-open');
      return undefined;
    }
    document.body.classList.add('sb-audio-player-dock-open');
    return () => document.body.classList.remove('sb-audio-player-dock-open');
  }, [isModal, minimized]);

  const handleMinimize = useCallback(() => {
    setMinimized(true);
  }, [setMinimized]);

  const handleExpand = useCallback(() => {
    setMinimized(false);
  }, [setMinimized]);

  const finishCloseSession = useCallback(() => {
    setCloseConfirmOpen(false);
    setMinimized(false);
    onSessionClose?.();
  }, [onSessionClose, setMinimized]);

  const handleCloseSession = useCallback(() => {
    const api = playerApiRef.current;
    if (api?.playing) {
      api.togglePlay?.();
    }
    if (isModal) {
      setCloseConfirmOpen(true);
      return;
    }
    finishCloseSession();
  }, [finishCloseSession, isModal]);

  const requestClose = useCallback(() => {
    if (closeConfirmOpen) return;
    setCloseConfirmOpen(true);
  }, [closeConfirmOpen]);

  const cancelClose = useCallback(() => {
    setCloseConfirmOpen(false);
  }, []);

  const handleBrowsePlayNow = useCallback(
    (item) => {
      onFileSelected?.(item);
      if (mobileLayout) setMobilePane('player');
    },
    [mobileLayout, onFileSelected]
  );

  const trackTitle = playlist.currentStorageItem?.name || fileItem?.name || 'Audio';

  const dockMetaLine = useMemo(() => {
    const parts = [];
    if (sleepDockLine) parts.push(sleepDockLine);
    if (playlist.tracks.length > 1) {
      parts.push(`${playlist.tracks.length} tracks in queue`);
    }
    return parts.join(' · ');
  }, [sleepDockLine, playlist.tracks.length]);

  useEffect(() => {
    if (!minimized) {
      setSleepDockLine('');
      return undefined;
    }
    const tick = () => {
      const api = playerApiRef.current;
      if (!api?.sleepTimerActive) {
        setSleepDockLine('');
        return;
      }
      const label = api.sleepTimerRemainingLabel;
      if (label === 'End of song' || label === 'End of track') {
        setSleepDockLine('Sleep: end of song');
      } else if (label === 'End of queue') {
        setSleepDockLine('Sleep: end of queue');
      } else if (label) {
        setSleepDockLine(`Sleep in ${label}`);
      } else {
        setSleepDockLine('');
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [minimized]);

  useEffect(() => {
    if (!minimized) {
      document.documentElement.style.removeProperty('--products-footer-clearance');
      return undefined;
    }
    const syncFooterClearance = () => {
      const footer =
        document.querySelector('.products-footer') ||
        document.querySelector('.posts-page-footer');
      if (!footer) return;
      const height = Math.ceil(footer.getBoundingClientRect().height);
      document.documentElement.style.setProperty(
        '--products-footer-clearance',
        `${Math.max(height, 52)}px`
      );
    };
    syncFooterClearance();
    window.addEventListener('resize', syncFooterClearance);
    return () => {
      window.removeEventListener('resize', syncFooterClearance);
      document.documentElement.style.removeProperty('--products-footer-clearance');
    };
  }, [minimized]);

  const rootClass = [
    'audio-player-page__file-session',
    'audio-player-page__storage-studio',
    minimized && mobileLayout ? 'audio-player-page__file-session--minimized' : '',
    mobileLayout ? `sb-audio-player-modal--pane-${mobilePane}` : 'sb-audio-player-modal--studio',
  ]
    .filter(Boolean)
    .join(' ');

  const desktopMinimizedDock = minimized && !mobileLayout;
  const studioShellClass = [
    rootClass,
    minimized ? 'storage-box-audio-studio--kept-alive' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const playerNode = fileItem?.itemId ? (
    <StorageBoxFileAudioPlayer
      fileItem={playlist.currentStorageItem}
      className="audio-player--sb-modal audio-player--sb-modal--storagebox"
      onQueueAdvance={playlist.advanceTrack}
      onPlayStateChange={setDockPlaying}
      playerApiRef={playerApiRef}
      queueHasNextTrack={playlist.hasNextTrackInQueue}
      autoPlayRequest={playlist.autoPlayRequest}
      equalizerGains={playlist.eqGains}
      onEqualizerGainsChange={playlist.setEqGains}
      onEqualizerPanelChange={null}
      onWindowMinimize={mobileLayout ? handleMinimize : null}
      onWindowClose={mobileLayout && onSessionClose ? handleCloseSession : null}
    />
  ) : (
    <div className="audio-player-page__storage-studio-empty">
      <p className="audio-player-page__storage-studio-empty-title">Nothing playing yet</p>
      <p className="audio-player-page__storage-studio-empty-hint">
        Pick an audio file from <strong>Storage Box</strong> on the right to start listening.
      </p>
    </div>
  );

  const playlistPanel = (
    <AudioPlayerPlaylistPanel
      playlistName={playlist.playlistName}
      onPlaylistNameChange={playlist.setPlaylistName}
      tracks={playlist.tracks}
      currentTrackId={playlist.currentTrackId}
      shuffle={playlist.shuffle}
      onShuffleChange={playlist.setShuffle}
      repeat={playlist.repeat}
      onRepeatChange={playlist.setRepeat}
      onAddCurrentTrack={playlist.addCurrentTrack}
      onSelectTrack={playlist.selectTrack}
      onRemoveTrack={(trackId) => {
        playlist.setTracks((prev) => {
          const next = prev.filter((t) => t.trackId !== trackId);
          if (playlist.currentTrackId === trackId) {
            playlist.setCurrentTrackId(next[0]?.trackId || '');
          }
          return next;
        });
      }}
      onLoadTracks={(loaded, meta) => playlist.loadTracks(loaded, meta)}
      linkedSavedPlaylistId={initialPlaylist?.meta?.id}
      eqGains={playlist.eqGains}
      statusMessage={playlist.status}
      onStatusMessage={playlist.setStatus}
    />
  );

  const libraryNode = (
    <StorageBoxAudioBrowser
      className="sb-audio-browser--studio"
      onSelectFile={handleBrowsePlayNow}
      onQueuePlayNext={(item) => {
        onFileSelected?.(item);
        playlist.queueTrackPlayNext(item);
      }}
    />
  );

  const miniDock = (
    <AudioPlayerMiniDock
      title={trackTitle}
      metaLine={dockMetaLine}
      playing={dockPlaying}
      onTogglePlay={() => playerApiRef.current?.togglePlay?.()}
      onRestore={handleExpand}
      onClose={isModal ? requestClose : onSessionClose ? handleCloseSession : null}
    />
  );

  const desktopDockPortal =
    desktopMinimizedDock && typeof document !== 'undefined'
      ? createPortal(
          <div className="audio-player-page__dock-host">{miniDock}</div>,
          document.body
        )
      : null;

  const useColHeadWindowActions = !isModal || mobileLayout;

  const studioLayout = (
    <StorageBoxAudioStudioLayout
      mobileLayout={mobileLayout}
      mobilePane={mobilePane}
      onMobilePaneChange={setMobilePane}
      trackCount={playlist.tracks.length}
      player={playerNode}
      playlistPanel={playlistPanel}
      library={libraryNode}
      paneClassPrefix="sb-audio-player-modal"
      onMinimize={useColHeadWindowActions ? handleMinimize : null}
      onClose={
        useColHeadWindowActions && onSessionClose
          ? isModal
            ? requestClose
            : handleCloseSession
          : null
      }
      playerEmbedsWindowActions={Boolean(fileItem?.itemId)}
    />
  );

  const overlayClassName = useMemo(
    () =>
      [
        'sb-audio-player-modal-overlay',
        mobileLayout && !minimized ? 'sb-audio-player-modal-overlay--sheet' : '',
        minimized ? 'sb-audio-player-modal-overlay--minimized' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [mobileLayout, minimized]
  );

  const contentClassName = useMemo(
    () =>
      [
        'sb-audio-player-modal',
        mobileLayout && !minimized ? 'sb-audio-player-modal--sheet' : '',
        mobileLayout && !minimized ? `sb-audio-player-modal--pane-${mobilePane}` : '',
        minimized ? 'sb-audio-player-modal--minimized' : '',
        !mobileLayout && !minimized ? 'sb-audio-player-modal--studio' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [mobileLayout, minimized, mobilePane]
  );

  const contentStyle = useMemo(() => {
    if (minimized) {
      return {
        width: '100%',
        maxWidth: '100%',
        height: 'auto',
        maxHeight: 'none',
        minHeight: 0,
        margin: 0,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      };
    }
    if (mobileLayout) {
      return {
        width: '100%',
        maxWidth: '100%',
        height: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
        maxHeight: 'calc(100dvh - var(--app-audio-sheet-top, 0px))',
        minHeight: 0,
        margin: 0,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      };
    }
    return {
      width: 'min(1180px, calc(100vw - 40px))',
      height: 'min(86dvh, 760px)',
      maxHeight: 'min(86dvh, 760px)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      borderRadius: '22px',
    };
  }, [mobileLayout, minimized]);

  const headerActions = useMemo(() => {
    if (!isModal || minimized || mobileLayout || !onSessionClose) return null;
    return (
      <div className="sb-audio-player-modal__chrome-row">
        <div className="sb-audio-player-modal__title-brand">
          <TbHeadphones className="sb-audio-player-modal__title-icon" aria-hidden />
          <span className="sb-audio-player-modal__title-text">Audio Player</span>
        </div>
        <div className="sb-audio-player-modal__col-head-actions">
          <AudioPlayerHeadWindowActions onMinimize={handleMinimize} onClose={requestClose} />
        </div>
      </div>
    );
  }, [isModal, minimized, mobileLayout, onSessionClose, handleMinimize, requestClose]);

  if (isModal) {
    const modalOpen = Boolean(fileItem?.itemId);
    return (
      <>
        <ConfirmModal
          isOpen={closeConfirmOpen}
          onClose={cancelClose}
          onConfirm={finishCloseSession}
          title="Close audio player?"
          subtitle={playlist.currentStorageItem?.name || fileItem?.name}
          description="Stop playback and close the player? Your queue and saved playlists are kept—you can reopen this file anytime."
          confirmLabel="Yes"
          cancelLabel="Cancel"
          icon={TbHeadphones}
        />
        {desktopDockPortal}
        <CenterModalWindow
          isOpen={modalOpen}
          onClose={requestClose}
          width={mobileLayout ? '100%' : 'min(1180px, calc(100vw - 40px))'}
          height="auto"
          swipeToClose={false}
          overlayClassName={overlayClassName}
          contentClassName={contentClassName}
          contentStyle={contentStyle}
          headerActions={headerActions}
          closeButtonClassName="sb-audio-player-modal__close--hidden"
        >
          <div className="sb-audio-player-modal__body">
            {minimized && mobileLayout ? miniDock : null}
            {!minimized && mobileLayout ? (
              <div className="sb-audio-player-modal__grabber" aria-hidden />
            ) : null}
            <div className={studioShellClass} aria-hidden={minimized}>
              {studioLayout}
            </div>
          </div>
        </CenterModalWindow>
      </>
    );
  }

  return (
    <>
      {desktopDockPortal}
      {minimized && mobileLayout ? (
        <div className={rootClass}>{miniDock}</div>
      ) : null}

      <div className={studioShellClass} aria-hidden={minimized}>
        {studioLayout}
      </div>
    </>
  );
}
